<template>
	<div class="income-spend-chart-container">
		<v-chart
			v-if="chartOption"
			ref="chart"
			:option="chartOption"
			:style="{ height: '200px', width: '100%' }"
			autoresize
		/>
		<div v-else class="no-data">No data available</div>
	</div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import VChart from 'vue-echarts';

use([CanvasRenderer, BarChart, GridComponent, TooltipComponent, LegendComponent]);

export default {
	name: 'IncomeVsSpendChart',
	components: { VChart },
	props: {
		monthlyData: { type: Array, required: true },
		formatter: { type: Object, required: true },
		colourMode: { type: String, default: 'light' }
	},
	computed: {
		filteredData() {
			if (!this.monthlyData) return [];
			return this.monthlyData.filter(d => !/^\d{4}$/.test(d.label));
		},
		chartOption() {
			if (!this.filteredData || this.filteredData.length === 0) return null;

			const textColor = this.colourMode === 'dark' ? '#e0e0e0' : '#333';
			const gridColor = this.colourMode === 'dark' ? '#333' : '#e0e0e0';

			const labels = this.filteredData.map(d => d.label);
			const income = this.filteredData.map(d => d[0]);
			const spend = this.filteredData.map(d => (d[1] || 0) + (d[2] || 0) + (d[3] || 0));

			return {
				tooltip: {
					trigger: 'axis',
					axisPointer: { type: 'shadow' },
					formatter: (params) => {
						let result = `<strong>${params[0].axisValue}</strong><br/>`;
						params.forEach(p => {
							result += `${p.marker} ${p.seriesName}: ${this.formatter.format(p.value)}<br/>`;
						});
						return result;
					}
				},
				legend: {
					data: ['Income', 'Spend'],
					bottom: 0,
					textStyle: { color: textColor }
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '15%',
					top: '10%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					data: labels,
					axisLine: { lineStyle: { color: gridColor } },
					axisLabel: { color: textColor }
				},
				yAxis: {
					type: 'value',
					axisLine: { lineStyle: { color: gridColor } },
					splitLine: { lineStyle: { color: gridColor } },
					axisLabel: {
						color: textColor,
						formatter: (value) => {
							if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
							return value;
						}
					}
				},
				series: [
					{
						name: 'Income',
						type: 'bar',
						data: income,
						itemStyle: {
							color: '#B4D8D7',
							borderRadius: [4, 4, 0, 0]
						},
						barMaxWidth: 40
					},
					{
						name: 'Spend',
						type: 'bar',
						data: spend,
						itemStyle: {
							color: '#413655',
							borderRadius: [4, 4, 0, 0]
						},
						barMaxWidth: 40
					}
				]
			};
		}
	}
};
</script>

<style scoped>
.income-spend-chart-container {
	width: 100%;
}

.no-data {
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--light-text-secondary);
}
</style>
